/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.7.5
@license MIT
@copyright (c) 2017, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _extends = _interopDefault(require('babel-runtime/helpers/extends'));
var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Promise = _interopDefault(require('babel-runtime/core-js/promise'));
var _typeof = _interopDefault(require('babel-runtime/helpers/typeof'));
var _isEqual = _interopDefault(require('lodash/fp/isEqual'));
var __ol = _interopDefault(require('openlayers'));
var Vue = _interopDefault(require('vue'));
var Observable = require('rxjs/Observable');
var merge = require('rxjs/observable/merge');
var throttleTime = require('rxjs/operator/throttleTime');
var distinctUntilChanged = require('rxjs/operator/distinctUntilChanged');
var map = require('rxjs/operator/map');
var core = require('../core');
var _toConsumableArray = _interopDefault(require('babel-runtime/helpers/toConsumableArray'));
var _get = _interopDefault(require('lodash/fp/get'));
var _noop = _interopDefault(require('lodash/fp/noop'));
var _isPlainObject = _interopDefault(require('lodash/fp/isPlainObject'));
var distinctUntilKeyChanged = require('rxjs/operator/distinctUntilKeyChanged');

/**
 * @module map/map
 */
/**
 * @vueProps
 */
var Map = __ol.Map;
var VectorLayer = __ol.layer.Vector;
var VectorSource = __ol.source.Vector;
var olcontrol = __ol.control;
var props = /** @lends module:map/map# */{
  /**
   * Options for default controls added to the map by default. Set to `false` to disable all map controls. Object
   * value is used to configure controls.
   * @type {Object|boolean}
   * @todo remove when vl-control-* components will be ready
   */
  controls: {
    type: [Object, Boolean],
    default: true
  },
  /**
   * The element to listen to keyboard events on. For example, if this option is set to `document` the keyboard
   * interactions will always trigger. If this option is not specified, the element the library listens to keyboard
   * events on is the component root element.
   * @type {string|Element|Document}
   */
  keyboardEventTarget: [String, Element, Document],
  /**
   * When set to `true`, tiles will be loaded during animations.
   * @type {boolean}
   */
  loadTilesWhileAnimating: {
    type: Boolean,
    default: false
  },
  /**
   * When set to `true`, tiles will be loaded while interacting with the map.
   * @type {boolean}
   */
  loadTilesWhileInteracting: {
    type: Boolean,
    default: false
  },
  /**
   * The map logo. If a **string** is provided, it will be set as the image source of the logo. If an **object** is provided,
   * the `src` property should be the **URL** for an image and the `href` property should be a **URL** for creating a link.
   * If an **element** is provided, the **element** will be used. To disable the map logo, set the option to `false`.
   * By default, the **OpenLayers** logo is shown.
   * @type {boolean|string|Object|Element}
   */
  logo: [String, Object, Element, Boolean],
  /**
   * The minimum distance in pixels the cursor must move to be detected as a map move event instead of a click.
   * Increasing this value can make it easier to click on the map.
   * @type {Number}
   */
  moveTolerance: {
    type: Number,
    default: 1
  },
  /**
   * The ratio between physical pixels and device-independent pixels (dips) on the device.
   * @type {number}
   */
  pixelRatio: {
    type: Number,
    default: 1
  },
  /**
   * Renderer. By default, **Canvas** and **WebGL** renderers are tested for support in that order,
   * and the first supported used. **Note** that the **Canvas** renderer fully supports vector data,
   * but **WebGL** can only render **Point** geometries.
   * @type {string|string[]}
   * @default ['canvas', 'webgl']
   */
  renderer: {
    type: [String, Array],
    default: function _default() {
      return [core.RENDERER_TYPE.CANVAS, core.RENDERER_TYPE.WEBGL];
    }
  },
  /**
   * Root element `tabindex` attribute value. Value should be provided to allow keyboard events on map.
   * @type {number|string}
   */
  tabindex: [String, Number]

  /**
   * @vueMethods
   */
};var methods = /** @lends module:map/map# */{
  /**
   * @return {ol.Map}
   * @protected
   */
  createOlObject: function createOlObject() {
    var map$$1 = new Map({
      controls: [],
      loadTilesWhileAnimating: this.loadTilesWhileAnimating,
      loadTilesWhileInteracting: this.loadTilesWhileInteracting,
      pixelRatio: this.pixelRatio,
      renderer: this.renderer,
      logo: this.logo,
      keyboardEventTarget: this.keyboardEventTarget,
      view: this._view
    });
    // ol.Map constructor can create default view if no provided with options
    this._view = map$$1.getView();
    // add default overlay to map
    this._defaultLayer.setMap(map$$1);

    if (this.controls) {
      var opts = _typeof(this.controls) === 'object' ? this.controls : undefined;
      map$$1.getControls().extend(olcontrol.defaults(opts).getArray());
    }

    return map$$1;
  },

  /**
   * @return {{
   *    hasLayer: function(ol.Layer): boolean,
   *    addLayer: function(ol.Layer),
   *    removeLayer: function(ol.Layer)
   *  }|undefined}
   *  @protected
   */
  getLayersTarget: function getLayersTarget() {
    if (!this.$map) return;

    var map$$1 = this.$map;
    var layers = this.$map.getLayers();

    return {
      hasLayer: function hasLayer(layer) {
        return layers.getArray().includes(layer);
      },
      addLayer: function addLayer(layer) {
        map$$1.addLayer(layer);
      },
      removeLayer: function removeLayer(layer) {
        map$$1.removeLayer(layer);
      }
    };
  },

  /**
   * @return {{
   *     hasInteraction: function(ol.interaction.Interaction): bool,
   *     addInteraction: function(ol.interaction.Interaction): void,
   *     removeInteraction: function(ol.interaction.Interaction): void
   *   }|undefined}
   * @protected
   */
  getInteractionsTarget: function getInteractionsTarget() {
    if (!this.$map) return;

    var map$$1 = this.$map;
    var interactions = this.$map.getInteractions();

    return {
      hasInteraction: function hasInteraction(interaction) {
        return interactions.getArray().includes(interaction);
      },
      addInteraction: function addInteraction(interaction) {
        map$$1.addInteraction(interaction);
      },
      removeInteraction: function removeInteraction(interaction) {
        map$$1.removeInteraction(interaction);
      }
    };
  },

  /**
   * @return {{
   *     addFeature: function(ol.Feature): void,
   *     removeFeature: function(ol.Feature): void,
   *     hasFeature: function(ol.Feature): bool
   *   }|undefined}
   * @protected
   */
  getFeaturesTarget: function getFeaturesTarget() {
    var source = this._defaultLayer.getSource();

    return {
      hasFeature: function hasFeature(feature) {
        return source.getFeatureById(feature.getId()) != null;
      },
      addFeature: function addFeature(feature) {
        source.addFeature(feature);
      },
      removeFeature: function removeFeature(feature) {
        source.removeFeature(feature);
      }
    };
  },

  /**
   * @return {{
   *     hasOverlay: function(ol.Overlay): bool,
   *     addOverlay: function(ol.Overlay): void,
   *     removeOverlay: function(ol.Overlay): void
   *   }|undefined}
   * @protected
   */
  getOverlaysTarget: function getOverlaysTarget() {
    if (!this.$map) return;

    var map$$1 = this.$map;
    var overlays = this.$map.getOverlays();

    return {
      hasOverlay: function hasOverlay(interaction) {
        return overlays.getArray().includes(interaction);
      },
      addOverlay: function addOverlay(interaction) {
        map$$1.addOverlay(interaction);
      },
      removeOverlay: function removeOverlay(interaction) {
        map$$1.removeOverlay(interaction);
      }
    };
  },

  /**
   * @param {number[]} pixel
   * @return {number[]} Coordinates in EPSG:4326
   */
  getCoordinateFromPixel: function getCoordinateFromPixel(pixel) {
    core.assert.hasMap(this);
    core.assert.hasView(this);

    return core.projHelper.toLonLat(this.$map.getCoordinateFromPixel(pixel), this.$view.getProjection());
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return core.mergeDescriptors(core.olCmp.methods.getServices.call(this), core.layersContainer.methods.getServices.call(this), core.interactionsContainer.methods.getServices.call(this), core.overlaysContainer.methods.getServices.call(this), core.featuresContainer.methods.getServices.call(this), {
      get map() {
        return vm.$map;
      },
      get view() {
        return vm.$view;
      },
      get viewContainer() {
        return vm;
      }
    });
  },

  /**
   * Triggers focus on map container.
   * @return {void}
   */
  focus: function focus() {
    this.$el.focus();
  },

  /**
   * @param {number[]} pixel
   * @param {function((ol.Feature|ol.render.Feature), ?ol.layer.Layer): *} callback
   * @param {Object} [opts]
   * @return {*|undefined}
   */
  forEachFeatureAtPixel: function forEachFeatureAtPixel(pixel, callback) {
    var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    core.assert.hasMap(this);

    return this.$map.forEachFeatureAtPixel(pixel, callback, opts);
  },

  /**
   * @param {number[]} pixel
   * @param {function(ol.layer.Layer, ?(number[]|Uint8Array)): *} callback
   * @param {function(ol.layer.Layer): boolean} [layerFilter]
   * @return {*|undefined}
   */
  forEachLayerAtPixel: function forEachLayerAtPixel(pixel, callback, layerFilter) {
    core.assert.hasMap(this);

    return this.$map.forEachLayerAtPixel(pixel, callback, undefined, layerFilter);
  },

  /**
   * @param {ol.View|Vue|undefined} view
   * @return {void}
   * @protected
   */
  setView: function setView(view) {
    view = view instanceof Vue ? view.$view : view;

    if (view !== this._view) {
      this._view = view;
    }
    if (this.$map && view !== this.$map.getView()) {
      this.$map.setView(view);
    }
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    core.assert.hasMap(this);
    this.$map.setTarget(this.$el);
    this.subscribeAll();
    this.updateSize();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    core.assert.hasMap(this);
    this.unsubscribeAll();
    this.$map.setTarget(undefined);
  },

  /**
   * Updates map size and re-renders map.
   * @return {Promise}
   */
  refresh: function refresh() {
    this.updateSize();
    return this.render();
  },

  /**
   * @return {Promise}
   */
  render: function render() {
    var _this = this;

    return new _Promise(function (resolve) {
      core.assert.hasMap(_this);

      _this.$map.once('postrender', function () {
        return resolve();
      });
      _this.$map.render();
    });
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToMapEvents.call(this);
  },

  /**
   * Updates map size.
   * @return {void}
   */
  updateSize: function updateSize() {
    core.assert.hasMap(this);
    this.$map.updateSize();
  }
};

/**
 * Container for **layers**, **interactions**, **controls** and **overlays**. It responsible for viewport
 * rendering and low level interaction events.
 *
 * @title vl-map
 * @alias module:map/map
 * @vueProto
 *
 * @fires module:map/map#click
 * @fires module:map/map#dblclick
 * @fires module:map/map#singleclick
 * @fires module:map/map#pointerdrag
 * @fires module:map/map#pointermove
 * @fires module:map/map#movestart
 * @fires module:map/map#moveend
 * @fires module:map/map#postrender
 * @fires module:map/map#precompose
 * @fires module:map/map#postcompose
 *
 * @vueSlot default Default slot for all child components.
 */
var Map$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('div', { class: [_vm.$options.name], attrs: { "tabindex": _vm.tabindex } }, [_vm._t("default")], 2);
  }, staticRenderFns: [],
  name: 'vl-map',
  mixins: [core.olCmp, core.layersContainer, core.interactionsContainer, core.overlaysContainer, core.featuresContainer],
  props: props,
  methods: methods,
  /**
   * @this module:map/map
   */
  created: function created() {
    var _this2 = this;

    /**
     * @type {ol.View|undefined}
     * @private
     */
    this._view = undefined;
    /**
     * @type {ol.layer.Vector}
     * @private
     */
    this._defaultLayer = new VectorLayer({
      source: new VectorSource()
    });

    _Object$defineProperties(this, /** @lends module:map/map# */{
      /**
       * OpenLayers map instance.
       * @type {ol.Map|undefined}
       */
      $map: {
        enumerable: true,
        get: function get() {
          return _this2.$olObject;
        }
      },
      /**
       * OpenLayers view instance.
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this2._view;
        }
      }
    });
  },

  /**
   * @this module:map/map
   */
  destroyed: function destroyed() {
    this._view = undefined;
  }
};

/**
 * Subscribe to OL map events.
 *
 * @this module:map/map
 * @return {void}
 * @private
 */
function subscribeToMapEvents() {
  var _context,
      _this3 = this;

  core.assert.hasMap(this);
  core.assert.hasView(this);

  var ft = 100;
  // pointer
  var pointerEvents = (_context = merge.merge.call(Observable.Observable, core.observableFromOlEvent(this.$map, ['click', 'dblclick', 'singleclick']), (_context = (_context = core.observableFromOlEvent(this.$map, ['pointerdrag', 'pointermove']), throttleTime.throttleTime).call(_context, ft), distinctUntilChanged.distinctUntilChanged).call(_context, function (a, b) {
    return _isEqual(a.coordinate, b.coordinate);
  })), map.map).call(_context, function (evt) {
    return _extends({}, evt, {
      coordinate: core.projHelper.toLonLat(evt.coordinate, _this3.$view.getProjection())
    });
  });
  // other
  var otherEvents = core.observableFromOlEvent(this.$map, ['movestart', 'moveend', 'postrender', 'precompose', 'postcompose']);

  var events = merge.merge.call(Observable.Observable, pointerEvents, otherEvents);

  this.subscribeTo(events, function (evt) {
    return _this3.$emit(evt.type, evt);
  });
}

/**
 * A click with no dragging. A double click will fire two of this.
 * @event module:map/map#click
 * @type {ol.MapBrowserEvent}
 */
/**
 * A true double click, with no dragging.
 * @event module:map/map#dblclick
 * @type {ol.MapBrowserEvent}
 */

/**
 * @module map/view
 */
/**
 * @vueProps
 */
var View = __ol.View;
var props$1 = /** @lends module:map/view# */{
  /**
   * The center coordinate of the map view in **EPSG:4326** projection.
   * @type {number[]}
   * @default [0, 0]
   * @vueSync
   */
  center: {
    type: Array,
    default: function _default() {
      return [0, 0];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  constrainRotation: {
    type: [Boolean, Number],
    default: true
  },
  enableRotation: {
    type: Boolean,
    default: true
  },
  /**
   * The extent that constrains the center defined in in **EPSG:4326** projection,
   * in other words, center cannot be set outside this extent.
   * @default undefined
   */
  extent: {
    type: Array,
    validator: function validator(value) {
      return value.length === 4;
    }
  },
  maxResolution: Number,
  minResolution: Number,
  /**
   * @default 28
   */
  maxZoom: {
    type: Number,
    default: core.MAX_ZOOM
  },
  /**
   * @default 0
   */
  minZoom: {
    type: Number,
    default: core.MIN_ZOOM
  },
  /**
   * @default EPSG:4326
   */
  projection: {
    type: String,
    default: core.EPSG_3857
  },
  resolution: Number,
  resolutions: Array,
  /**
   * The initial rotation for the view in **radians** (positive rotation clockwise).
   * @type {number}
   * @vueSync
   */
  rotation: {
    type: Number,
    default: 0
  },
  /**
   * Zoom level used to calculate the resolution for the view as `int` value. Only used if `resolution` is not defined.
   * @type {number}
   * @default 0
   * @vueSync
   */
  zoom: {
    type: Number,
    default: core.MIN_ZOOM
  },
  /**
   * @default 2
   */
  zoomFactor: {
    type: Number,
    default: core.ZOOM_FACTOR
  }

  /**
   * @vueComputed
   */
};var computed = /** @lends module:map/view# */{
  currentCenter: function currentCenter() {
    if (this.rev && this.$view) {
      return getCenter.call(this);
    }

    return this.center;
  },
  currentZoom: function currentZoom() {
    if (this.rev && this.$view) {
      return getZoom.call(this);
    }

    return this.zoom;
  },
  currentRotation: function currentRotation() {
    if (this.rev && this.$view) {
      return this.$view.getRotation();
    }

    return this.rotation;
  },
  currentResolution: function currentResolution() {
    if (this.rev && this.$view) {
      return this.$view.getResolution();
    }

    return this.resolution;
  }
};

/**
 * @vueMethods
 */
var methods$1 = /** @lends module:map/view# */{
  /**
   * @see {@link https://openlayers.org/en/latest/apidoc/ol.View.html#animate}
   * @param {...(olx.AnimationOptions|function(boolean))} args
   * @return {Promise} Resolves when animation completes
   */
  animate: function animate() {
    var _this = this;

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    core.assert.hasView(this);

    var cb = args.reverse().find(function (x) {
      return typeof x === 'function';
    }) || _noop;
    args.forEach(function (opts) {
      var center = _get('center', opts);
      if (Array.isArray(center) && center.length) {
        opts.center = core.projHelper.fromLonLat(center, _this.projection);
      }
    });

    return new _Promise(function (resolve) {
      var _$view;

      return (_$view = _this.$view).animate.apply(_$view, _toConsumableArray(args).concat([function (complete) {
        cb(complete);
        resolve(complete);
      }]));
    });
  },

  /**
   * @return {ol.View}
   * @protected
   */
  createOlObject: function createOlObject() {
    return new View({
      center: core.projHelper.fromLonLat(this.center, this.projection),
      constrainRotation: this.constrainRotation,
      enableRotation: this.enableRotation,
      extent: this.extent ? core.projHelper.extentFromLonLat(this.extent, this.projection) : undefined,
      maxResolution: this.maxResolution,
      minResolution: this.minResolution,
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      projection: this.projection,
      resolution: this.resolution,
      resolutions: this.resolutions,
      rotation: this.rotation,
      zoom: this.zoom,
      zoomFactor: this.zoomFactor
    });
  },

  /**
   * @see {@link https://openlayers.org/en/latest/apidoc/ol.View.html#fit}
   * @param {GeoJSONFeature|ol.Extent|ol.geom.Geometry|Vue} geometryOrExtent
   * @param {olx.view.FitOptions} [options]
   * @return {Promise} Resolves when view changes
   */
  fit: function fit(geometryOrExtent) {
    var _this2 = this;

    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    core.assert.hasView(this);

    // transform to GeoJSON, vl-feature to ol.Feature
    if (_isPlainObject(geometryOrExtent)) {
      geometryOrExtent = core.geoJsonHelper.readGeometry(geometryOrExtent, this.$view.getProjection());
    } else if (geometryOrExtent instanceof Vue) {
      geometryOrExtent = geometryOrExtent.$geometry;
    } else if (Array.isArray(geometryOrExtent)) {
      geometryOrExtent = core.projHelper.extentFromLonLat(geometryOrExtent, this.$view.getProjection());
    }

    var cb = options.callback || _noop;

    return new _Promise(function (resolve) {
      _this2.$view.fit(geometryOrExtent, _extends({}, options, {
        callback: function callback(complete) {
          cb(complete);
          resolve(complete);
        }
      }));
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$viewContainer && this.$viewContainer.setView(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$viewContainer && this.$viewContainer.setView(undefined);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToViewChanges.call(this);
  }
};

var watch = {
  center: function center(value) {
    if (this.$view && !_isEqual(value, getCenter.call(this))) {
      this.$view.setCenter(core.projHelper.fromLonLat(value, this.$view.getProjection()));
    }
  },
  resolution: function resolution(value) {
    if (this.$view && value !== this.$view.getResolution()) {
      this.$view.setResolution(value);
    }
  },
  zoom: function zoom(value) {
    value = Math.round(value);
    if (this.$view && value !== getZoom.call(this)) {
      this.$view.setZoom(value);
    }
  },
  rotation: function rotation(value) {
    if (this.$view && value !== this.$view.getRotation()) {
      this.$view.setRotation(value);
    }
  },
  minZoom: function minZoom(value) {
    if (this.$view && value !== this.$view.getMinZoom()) {
      this.$view.setMinZoom(value);
    }
  },
  maxZoom: function maxZoom(value) {
    if (this.$view && value !== this.$view.getMaxZoom()) {
      this.$view.setMaxZoom(value);
    }
  }
};

/**
 * Represents a simple **2D view** of the map. This is the component to act upon to change the **center**,
 * **resolution**, and **rotation** of the map.
 *
 * @title View `vl-view` component
 * @alias module:map/view
 * @vueProto
 *
 * @vueSlot default [scoped] Default scoped slot with current state: center, zoom, rotation & etc.
 */
var View$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" } }, [_vm._t("default", null, { center: _vm.currentCenter, zoom: _vm.currentZoom, resolution: _vm.currentResolution, rotation: _vm.currentRotation })], 2);
  }, staticRenderFns: [],
  name: 'vl-view',
  mixins: [core.olCmp],
  props: props$1,
  computed: computed,
  methods: methods$1,
  watch: watch,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  /**
   * @this module:map/view
   */
  created: function created() {
    var _this3 = this;

    _Object$defineProperties(this, /** @lends module:map/view# */{
      /**
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this3.$olObject;
        }
      },
      $viewContainer: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.viewContainer;
        }
      }
    });
  }
};

/**
 * Subscribe to OpenLayers significant events
 * @return {void}
 * @this module:map/view
 * @private
 */
function subscribeToViewChanges() {
  var _context,
      _this4 = this;

  core.assert.hasView(this);

  var ft = 0;
  var resolution = core.observableFromOlChangeEvent(this.$view, 'resolution', true, ft);
  var zoom = (_context = map.map.call(resolution, function () {
    return {
      prop: 'zoom',
      value: getZoom.call(_this4)
    };
  }), distinctUntilKeyChanged.distinctUntilKeyChanged).call(_context, 'value');

  var changes = merge.merge.call(Observable.Observable, core.observableFromOlChangeEvent(this.$view, 'center', true, ft, getCenter.bind(this)), core.observableFromOlChangeEvent(this.$view, 'rotation', true, ft), resolution, zoom);

  this.subscribeTo(changes, function (_ref) {
    var prop = _ref.prop,
        value = _ref.value;

    ++_this4.rev;
    _this4.$emit('update:' + prop, value);
  });
}

function getZoom() {
  return Math.round(this.$view.getZoom());
}

function getCenter() {
  return core.projHelper.toLonLat(this.$view.getCenter(), this.$view.getProjection());
}

/**
 * @module map
 */
/**
 * @alias module:map
 *
 * @example Install
 * import Vue from 'vue'
 * // import module and styles
 * import { Map } from 'vuelayers'
 * // or
 * import Map from 'vuelayers/lib/map'
 * // import VueLayers styles
 * import 'vuelayers/lib/style.css'
 * // register vl-map and vl-view components
 * Vue.use(Map)
 *
 * @vueExample Usage [html]
 * <vl-map :load-tiles-while-animating="true" :load-tiles-while-interacting="true" style="height: 400px">
 *   <vl-view :center="[0, 0]" :zoom="3" :rotation="0"></vl-view>
 *
 *   <vl-layer-tile>
 *     <vl-source-osm></vl-source-osm>
 *   </vl-layer-tile>
 * </vl-map>
 */
var index = {
  /**
   * Container for **layers**, **interactions**, **controls** and **overlays**. It responsible for viewport
   * rendering and low level interaction events.
   * @alias module:map/map
   */
  Map: Map$1,
  /**
   * Represents a simple **2D view** of the map. This is the component to act upon to change the **center**,
   * **resolution**, and **rotation** of the map.
   * @alias module:map/map
   */
  View: View$1,
  /**
   * Registers Map and View components with default names.
   *
   * @param {Vue} Vue
   * @return {void}
   */
  install: function install(Vue$$1) {
    Vue$$1.component(Map$1.name, Map$1);
    Vue$$1.component(View$1.name, View$1);
  }
};

module.exports = index;

//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/map/index.js
// module id = 1185
// module chunks = 1